/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from '../utils/loadable';
import { StatsBox } from '../components/StatsBox';
import { calculateTrends } from './calculateTrends';
import { incidentCountiesSelector, selectedRunTimeAtom, incidentSectionStats } from './atoms';
import { IncidentSection } from '../pages/IncidentPage';
import { debouncedViewStateAtom, exportStatsContainerRefAtom } from '../map/atoms';
import { cameraBbox } from '../map/geometryDetail';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { CenterFocusStrong, Clear } from '@mui/icons-material';
import { disabledModelsFor, settledIncidentConfigByIdAtom } from '../config/api';

// import { currentRegionState } from '../Regions/regionsSelector';
// import { regionState } from '../Regions/FemaRegions';
type Props = {
  section: IncidentSection;
  incidentId: string;
  viewType: string;
  hideFilters?: boolean;
};

export const filterByViewAtom = atom(false);

export const filterByCountyAtom = atom<string[]>([]);

export const filterByStateAtom = atom<string[]>([]);

export const filterByRegionAtom = atom<string[]>([]);

/**
 * The incident the current filter selection was made for. The filter atoms are
 * app-wide while their values mean something only for one incident, so ownership
 * is tracked here. An atom rather than a component ref because this panel unmounts
 * on the way through the dashboard, which is how the stale filter travelled.
 */
const filteredIncidentIdAtom = atom<string | null>(null);

type IncidentFilters = {
  counties: string[];
  states: string[];
  withinView: boolean;
};

const NO_FILTERS: IncidentFilters = { counties: [], states: [], withinView: false };

/**
 * The filter as it applies to one incident: the live selection while that incident
 * owns it, no filter otherwise. Every consumer reads it through here, so entering
 * an incident queries unfiltered from the first render. Clearing in an effect was a
 * render too late -- effects run after the render that already evaluated the stats
 * selectors, so one aggregation went out restricted to counties the new incident
 * never touched, and the tiles showed its zeros until it was replaced.
 */
export const incidentFiltersSelector = atomFamily((incidentId: string | undefined) =>
  atom((get): IncidentFilters => {
    if (incidentId == null || get(filteredIncidentIdAtom) !== incidentId) {
      return NO_FILTERS;
    }
    return {
      counties: get(filterByCountyAtom),
      states: get(filterByStateAtom),
      withinView: get(filterByViewAtom),
    };
  })
);

// State "select all" rows are surfaced as Select options carrying this prefixed
// value. The Select's onChange expands them into county selections, so a state
// row is never persisted as a real value -- this keeps all selection mutation on
// a single path and avoids `undefined` (the old no-value state row) corrupting
// the value array and white-screening the page.
const STATE_OPTION_PREFIX = '__state__:';

/**
 * Params that ask for nothing. incidentSectionStats answers an empty aggregation
 * with no request at all when it is given no incident or section, which is how a
 * panel that must not query yet holds still without a conditional hook.
 */
const NO_STATS_QUERY = {};

export const ExposureStats = React.memo(({ section, incidentId, viewType, hideFilters }: Props) => {
  // const currentRegion = useAtomValue(currentRegionState);
  // const femaRegionsJson = useAtomValue(loadable(regionState));
  const debounceViewState = useAtomValue(debouncedViewStateAtom);

  const statsContainerRef = React.useRef<HTMLDivElement>(null);
  const setExportStatsContainerRef = useSetAtom(exportStatsContainerRefAtom);

  React.useEffect(() => {
    setExportStatsContainerRef(statsContainerRef.current);
    return () => setExportStatsContainerRef(null);
  }, [setExportStatsContainerRef]);
  // const regionFeature = femaRegionsJson.contents?.features?.find(
  //   (f: any) => f?.properties?.region === currentRegion
  // );

  const setFilterByView = useSetAtom(filterByViewAtom);
  const setFilterByCounty = useSetAtom(filterByCountyAtom);
  const setFilterByState = useSetAtom(filterByStateAtom);
  // const [filterByRegion, setFilterByRegion] = useAtom(filterByRegionAtom);

  // What the panel renders and queries with: the selection only while this
  // incident owns it (see incidentFiltersSelector), so a selection made in
  // another incident is never in force here, not even for one render.
  const {
    counties: filterByCounty,
    states: filterByState,
    withinView: filterByView,
  } = useAtomValue(incidentFiltersSelector(incidentId));

  // Take ownership of the selection atoms for this incident, discarding whatever
  // the previous one left in them. Ownership changes only when the incident
  // does, so switching section tabs or resizing the panel (both of which remount
  // this component) keeps the selection.
  const [filteredIncidentId, setFilteredIncidentId] = useAtom(filteredIncidentIdAtom);
  React.useEffect(() => {
    if (filteredIncidentId === incidentId) {
      return;
    }
    setFilterByCounty([]);
    setFilterByState([]);
    setFilterByView(false);
    setFilteredIncidentId(incidentId);
  }, [
    incidentId,
    filteredIncidentId,
    setFilteredIncidentId,
    setFilterByCounty,
    setFilterByState,
    setFilterByView,
  ]);

  // Suppress the "Filter Stats By County" tooltip while the dropdown is open,
  // otherwise its popper (zIndex 1500) paints over the open menu (zIndex 1300).
  const [selectOpen, setSelectOpen] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  // The map's own bounds, which is why this reads the camera's published
  // dimensions rather than building a viewport without them: unsized,
  // WebMercatorViewport assumes a 1x1 pixel canvas and the "within view"
  // envelope collapses to a point, filtering the stats down to nothing. Null
  // until the map has measured its canvas.
  const cameraBounds = React.useMemo(() => {
    const bbox = cameraBbox(debounceViewState);
    if (bbox == null) {
      return null;
    }
    return { xmin: bbox[0], ymin: bbox[1], xmax: bbox[2], ymax: bbox[3] };
  }, [debounceViewState]);

  const bounds = filterByView ? (cameraBounds ?? undefined) : undefined;

  // The view filter is on and there is no envelope to apply it with yet. Asking
  // anyway -- which is what dropping the bbox from the request amounted to --
  // answers with the NATIONAL total and renders it in the tiles beneath a toggle
  // that reads as on, with nothing to tell the two apart. So nothing is requested
  // and the tiles show a loading state until the map publishes its canvas size,
  // which its own resize observer does within a frame of mounting.
  const viewFilterPending = filterByView && cameraBounds == null;

  // Same treatment as the camera: until the incident config has settled the
  // disabled models are unknown, and a query sent without them would put the
  // hidden runs' numbers in the tiles until the real answer replaced them.
  const incidentConfig = useAtomValue(loadable(settledIncidentConfigByIdAtom(incidentId)));
  const configPending = incidentConfig.state !== 'hasData';

  const disabledModels = React.useMemo(
    () =>
      section && incidentConfig.state === 'hasData'
        ? disabledModelsFor(incidentConfig.data, section)
        : [],
    [section, incidentConfig]
  );

  const sectionStatistics = useAtomValue(
    loadable(
      incidentSectionStats(
        viewFilterPending || configPending
          ? NO_STATS_QUERY
          : {
              sectionId: section,
              incidentId,
              geometry: bounds,
              counties: filterByCounty,
              states: filterByState,
              disabledModels,
            }
      )
    )
  );

  const range = useAtomValue(selectedRunTimeAtom(`${incidentId}-${section}`))?.time ?? 0;

  const currentRangeStatistics = React.useMemo(() => {
    if (sectionStatistics.state === 'hasData') {
      return calculateTrends(sectionStatistics.data);
    }
    return null;
  }, [sectionStatistics]);

  const currentRangeStates = currentRangeStatistics?.find(s => s.lastupdatedon === range);

  const exposedCounties = useAtomValue(
    loadable(incidentCountiesSelector({ incidentId, sectionId: section }))
  );

  const countiesByState = React.useMemo(
    () =>
      groupCountiesByState(
        exposedCounties.state === 'hasData' ? [...exposedCounties.data].sort() : []
      ),
    [exposedCounties]
  );

  // A state row arrives as a STATE_OPTION_PREFIX sentinel; expand it into that
  // state's counties (select-all when not already fully selected, otherwise
  // deselect-all). Counties pass through unchanged. The final Set dedupes, so
  // selecting a state whose counties are partly chosen can never produce the
  // duplicate values that previously crashed the multiple Select.
  const handleChange = React.useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      const raw = event.target.value as string[];
      setFilterByCounty(resolveCountySelection(raw, countiesByState, filterByCounty));
    },
    [countiesByState, filterByCounty, setFilterByCounty]
  );

  const { originalValues, previousValues } = currentRangeStates ?? {};

  const val = (field: string): number => Math.round(Number(originalValues?.[field] ?? 0));
  const prev = (field: string): number | undefined =>
    previousValues != null ? Math.round(Number(previousValues[field] ?? 0)) : undefined;

  const stats: Record<
    IncidentSection,
    {
      label: string;
      value: string | number;
      prevValue?: string | number;
    }[]
  > = {
    population: [
      {
        label: 'Exposed Population',
        value: val('ls_night_pop'),
        prevValue: prev('ls_night_pop'),
      },
      {
        label: 'Exposed Housing Units',
        value: val('housingunits'),
        prevValue: prev('housingunits'),
      },
      {
        label: 'Exposed Population\n age 65 and Older',
        value: val('pop65over'),
        prevValue: prev('pop65over'),
      },
      {
        label: 'Exposed Population with\n Limited English Proficiency',
        value: val('lep'),
        prevValue: prev('lep'),
      },
      {
        label: 'Exposed Households with\n Food Stamps / SNAP',
        value: val('householdswithfs'),
        prevValue: prev('householdswithfs'),
      },
      {
        label: 'Exposed Households with\n Public Assistance',
        value: val('householdswithpa'),
        prevValue: prev('householdswithpa'),
      },
    ],
    residential: [
      {
        label: 'Exposed Residential Structures',
        value: val('residential'),
        prevValue: prev('residential'),
      },
    ],
    nonResidentialStructures: [
      {
        label: 'Exposed Agricultural',
        value: val('agriculturalru'),
        prevValue: prev('agriculturalru'),
      },
      {
        label: 'Exposed Commercial\n (Offices)',
        value: val('commercialoff'),
        prevValue: prev('commercialoff'),
      },
      {
        label: 'Exposed Commercial\n (Retail)',
        value: val('commercialret'),
        prevValue: prev('commercialret'),
      },
      {
        label: 'Exposed Industrial',
        value: val('industrialgen'),
        prevValue: prev('industrialgen'),
      },
      {
        label: 'Exposed Government',
        value: val('exemptgovernm'),
        prevValue: prev('exemptgovernm'),
      },
      {
        label: 'Exposed Recreation',
        value: val('recreational'),
        prevValue: prev('recreational'),
      },
    ],
    communications: [
      {
        label: 'Cell Towers Out\n Power Outage',
        value: val('cell_sites_out_due_to_power'),
        prevValue: prev('cell_sites_out_due_to_power'),
      },
      {
        label: 'Cell Towers Out\n Transportation Outage',
        value: val('cell_sites_out_due_to_transpor'),
        prevValue: prev('cell_sites_out_due_to_transpor'),
      },
      {
        label: 'Cell Towers Out\n Damage',
        value: val('cell_sites_out_due_to_damage'),
        prevValue: prev('cell_sites_out_due_to_damage'),
      },
      {
        label: 'Population Affected\n by Cell Towers Out',
        value: val('populationaffected'),
        prevValue: prev('populationaffected'),
      },
    ],

    declarations: [
      {
        label: 'Total Counties Affected',
        value: val('is_affected'),
        prevValue: prev('is_affected'),
      },
      {
        label: 'Total Counties Designated\n for Disaster Assistance',
        value: val('is_designated'),
        prevValue: prev('is_designated'),
      },
    ],
  };

  if (stats[section].length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
      }}
    >
      <Box
        ref={statsContainerRef}
        sx={{
          display: 'flex',
          flexWrap: viewType === 'wowcast' ? 'wrap' : 'wrap',
          flexDirection: viewType === 'map' ? 'column' : 'row',
          justifyContent: 'center',

          '@media (max-height: 768px)': {
            flexDirection: 'row',
            width: viewType === 'map' ? '410px' : '100%',
          },
          '@media print': {
            maxWidth: '1200px',
          },
        }}
      >
        {viewFilterPending || configPending ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '120px',
              minWidth: '220px',
            }}
          >
            <CircularProgress
              color="primary"
              aria-label={
                viewFilterPending ? 'Waiting for the map view' : 'Waiting for the incident config'
              }
            />
          </Box>
        ) : (
          stats[section].map((stat, i) => <StatsBox {...stat} key={i} />)
        )}
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          width: 'fit-content',
          display: 'flex',
          flexDirection: 'row',
          m: 1,
          mt: 0,
          borderRadius: 2,
          '@media print': {
            display: 'none',
          },
        }}
      >
        {!hideFilters && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                height: 'fit-content',
                alignContent: 'center',
                justifyContent: 'center',
                mt: 1,
                borderRadius: 2,
                '@media print': {
                  display: 'none',
                },
              }}
            >
              <Tooltip title="Filter Stats Within View">
                <ToggleButton
                  value="check"
                  sx={{
                    border: '2px solid white',
                    height: 'fit-content',
                    color: 'white',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  size="small"
                  selected={filterByView}
                  color="primary"
                  onChange={() => {
                    setFilterByView(!filterByView);
                  }}
                  aria-label="Filter Stats Within View"
                >
                  {filterByView ? (
                    <CenterFocusStrong sx={{ mr: 0.5 }} />
                  ) : (
                    <CropFreeIcon sx={{ mr: 0.5 }} />
                  )}
                  Filter Within View
                </ToggleButton>
              </Tooltip>
              <Tooltip
                title="Filter Stats By County"
                disableInteractive
                open={tooltipOpen && !selectOpen}
                onOpen={() => setTooltipOpen(true)}
                onClose={() => setTooltipOpen(false)}
              >
                <FormControl
                  sx={{
                    minWidth: 180,
                    ml: 1,
                    alignContent: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    '& .MuiInputLabel-root': {
                      color: 'white',
                      // MUI tints the label with primary.main (dark blue) when the
                      // Select is focused/open; force it to stay readable on the
                      // dark panel.
                      '&.Mui-focused': {
                        color: 'white',
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'white',
                      },
                      '&:hover fieldset': {
                        borderColor: 'white',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: 'white',
                    },
                  }}
                >
                  <InputLabel size="small" id="demo-multiple-name-label" sx={{ color: 'white' }}>
                    Filter by County
                  </InputLabel>
                  <Select
                    multiple
                    size="small"
                    open={selectOpen}
                    onOpen={() => setSelectOpen(true)}
                    onClose={() => setSelectOpen(false)}
                    onChange={handleChange}
                    input={<OutlinedInput label="Filter Models" />}
                    MenuProps={MenuProps}
                    renderValue={selected => selected.join(', ')}
                    value={filterByCounty}
                    sx={{ maxWidth: 275, minWidth: 180 }}
                  >
                    {Object.entries(countiesByState).map(([state, counties]) => {
                      const allSelected =
                        counties.length > 0 && counties.every(c => filterByCounty.indexOf(c) > -1);
                      const someSelected = counties.some(c => filterByCounty.indexOf(c) > -1);
                      return [
                        <MenuItem key={`state-${state}`} value={STATE_OPTION_PREFIX + state}>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                          />
                          {state}
                        </MenuItem>,
                        ...counties.map(county => (
                          <MenuItem dense key={county} value={county}>
                            <Checkbox
                              sx={{ ml: 2 }}
                              checked={filterByCounty.indexOf(county) > -1}
                            />
                            {county}
                          </MenuItem>
                        )),
                      ];
                    })}
                  </Select>
                  {filterByCounty.length > 0 && (
                    <IconButton size="small" onClick={() => setFilterByCounty([])}>
                      <Clear />
                    </IconButton>
                  )}
                </FormControl>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
});

/**
 * Resolve the next county selection from a multiple-Select change. State rows
 * arrive as STATE_OPTION_PREFIX sentinels; each expands to select-all (when the
 * state isn't already fully selected) or deselect-all of its counties. The
 * result is de-duplicated so a partially-selected state can't reintroduce the
 * duplicate values that previously crashed the Select.
 */
export function resolveCountySelection(
  rawValues: string[],
  countiesByState: Record<string, string[]>,
  currentSelection: string[]
): string[] {
  const toggledStates = rawValues
    .filter(v => v.startsWith(STATE_OPTION_PREFIX))
    .map(v => v.slice(STATE_OPTION_PREFIX.length));
  let next = rawValues.filter(v => !v.startsWith(STATE_OPTION_PREFIX));
  for (const state of toggledStates) {
    const stateCounties = countiesByState[state] ?? [];
    const allSelected =
      stateCounties.length > 0 && stateCounties.every(c => currentSelection.includes(c));
    next = allSelected ? next.filter(c => !stateCounties.includes(c)) : [...next, ...stateCounties];
  }
  return Array.from(new Set(next));
}

function groupCountiesByState(counties: string[]) {
  const stateCounties: { [key: string]: string[] } = {};
  counties.forEach(county => {
    const state = county?.split?.(',')?.[1]?.trim?.();
    if (state != null && state !== '') {
      if (!stateCounties[state]) {
        stateCounties[state] = [];
      }
      stateCounties[state].push(county);
    }
  });
  return stateCounties;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  // 'menu' (not the default 'selectedMenu') stops MUI from scrolling the last
  // selected option into view on every value change -- selecting a state would
  // otherwise jump the menu down to that state's last county.
  variant: 'menu' as const,
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 300,
      },
    },
  },
};
